import { coerceCssPixelValue } from '@angular/cdk/coercion';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  FlexibleConnectedPositionStrategyOrigin,
  OverlayContainer,
  ViewportRuler,
} from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ViewportScrollPosition } from '@angular/cdk/scrolling';

/** Equivalent of `DOMRect` without some of the properties we don't care about. */
type Dimensions = Omit<DOMRect, 'x' | 'y' | 'toJSON'>;

export class CdkFlexibleConnectedPositionStrategy extends FlexibleConnectedPositionStrategy {
  constructor(
    private viewportRect: Dimensions,
    origin: FlexibleConnectedPositionStrategyOrigin,
    _viewportRuler: ViewportRuler,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _document: any,
    _platform: Platform,
    _overlayContainer: OverlayContainer,
  ) {
    super(origin, _viewportRuler, _document, _platform, _overlayContainer);

    // overwrite private member "_getNarrowedViewportRect" 🙀
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any)._getNarrowedViewportRect = () => {
      return this.viewportRect;
    };

    //overriding function to use this.viewportRect.width rather than document width which is by default
    //to emulate the overlay on the webview
    (this as any)._getExactOverlayX = (
      position: ConnectedPosition,
      originPoint: { x: number; y: number },
      scrollPosition: ViewportScrollPosition,
    ) => {
      const styles = { left: '', right: '' };
      let overlayPoint = (this as any)._getOverlayPoint(originPoint, (this as any)._overlayRect, position);

      if ((this as any)._isPushed) {
        overlayPoint = (this as any)._pushOverlayOnScreen(
          overlayPoint,
          (this as any)._overlayRect,
          scrollPosition,
        );
      }

      let horizontalStyleProperty;
      if ((this as any)._isRtl()) {
        horizontalStyleProperty = position.overlayX === 'end' ? 'left' : 'right';
      } else {
        horizontalStyleProperty = position.overlayX === 'end' ? 'right' : 'left';
      }

      if (horizontalStyleProperty === 'right') {
        const documentWidth = this.viewportRect.width;
        styles.right = `${documentWidth - (overlayPoint.x + (this as any)._overlayRect.width)}px`;
      } else {
        styles.left = coerceCssPixelValue(overlayPoint.x);
      }
      return styles;
    };

    //overriding function to use this.viewportRect.width rather than document width which is by default
    //to emulate the overlay on the webview
    (this as any)._getExactOverlayY = (
      position: ConnectedPosition,
      originPoint: { x: number; y: number },
      scrollPosition: ViewportScrollPosition,
    ) => {
      const styles = { top: '', bottom: '' } as CSSStyleDeclaration;
      let overlayPoint = (this as any)._getOverlayPoint(originPoint, (this as any)._overlayRect, position);

      if ((this as any)._isPushed) {
        overlayPoint = (this as any)._pushOverlayOnScreen(
          overlayPoint,
          (this as any)._overlayRect,
          scrollPosition,
        );
      }

      if (position.overlayY === 'bottom') {
        const documentHeight = this.viewportRect.height;
        styles.bottom = `${documentHeight - (overlayPoint.y + (this as any)._overlayRect.height)}px`;
      } else {
        styles.top = coerceCssPixelValue(overlayPoint.y);
      }

      return styles;
    };

    // overriding so we can remove the scale that has been applied to the overlayRect incorrectly
    (this as any)._getOverlayPoint = (
      originPoint: { x: number; y: number },
      overlayRect: Dimensions,
      pos: ConnectedPosition,
    ) => {
      let scale = 1.0;
      const frameWrapper = document.getElementById('frameWrapper');
      if (frameWrapper) {
        scale = Number(frameWrapper.style.scale);
      }
      // Calculate the (overlayStartX, overlayStartY), the start of the
      // potential overlay position relative to the origin point.
      // remove scale
      let overlayStartX: number;
      if (pos.overlayX == 'center') {
        overlayStartX = -(overlayRect.width / scale / 2);
      } else if (pos.overlayX === 'start') {
        overlayStartX = (this as any)._isRtl() ? -overlayRect.width : 0;
      } else {
        overlayStartX = (this as any)._isRtl() ? 0 : -overlayRect.width;
      }
      // remove scale
      let overlayStartY: number;
      if (pos.overlayY == 'center') {
        overlayStartY = -(overlayRect.height / scale / 2);
      } else {
        overlayStartY = pos.overlayY == 'top' ? 0 : -overlayRect.height;
      }

      // The (x, y) coordinates of the overlay.
      return {
        x: originPoint.x + overlayStartX,
        y: originPoint.y + overlayStartY,
      };
    };
    //overriding to stop the overlay trying to fit it within the visible screen, we want to allow the item to be off screen
    (this as any)._pushOverlayOnScreen = (start: { x: number; y: number }) => {
      return start;
    };
  }
}
